import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Lock } from 'lucide-react';
import Dialog from '../ui/Dialog';
import Field, { FormServerError } from '../ui/Field';
import { api } from '../../services/api';
import { toast } from '../../store/toastStore';
import EntityPicker from '../ui/EntityPicker';

/**
 * D-144 / D-145 (P-19a item 5) — "שליחה לעובד אחר": the account manager (or
 * any employee on the ticket) hands a service request to another employee,
 * with an optional INTERNAL note (yellow, "רק עובדי OTE רואים"). The ticket
 * leaves the sender and appears in his "קריאות לאחרים" tab with its
 * recipient and status; the employee gets a notification and a toast.
 */
export default function ForwardTicketDialog({ ticketId, ticketTitle, agents, currentUserId, onClose }: {
  ticketId: string;
  ticketTitle: string;
  agents: { id: string; name: string }[];
  currentUserId?: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [agentId, setAgentId] = useState('');
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);
  const options = agents.filter((a) => a.id !== currentUserId);

  const mutation = useMutation({
    mutationFn: () => api.forwardTicket(ticketId, agentId, note.trim() || undefined),
    onSuccess: () => {
      const name = options.find((a) => a.id === agentId)?.name ?? '';
      toast('success', t('ticketDetail.forward.done', { name }));
      queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      onClose();
    },
  });
  const missing = touched && !agentId;

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('ticketDetail.forward.title')}
      description={<bdi className="font-medium text-text">{ticketTitle}</bdi>}
      size="md"
      dismissible={!mutation.isPending}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={mutation.isPending}>{t('common.cancel')}</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={mutation.isPending}
            aria-busy={mutation.isPending || undefined}
            onClick={() => { setTouched(true); if (agentId) mutation.mutate(); }}
          >
            {mutation.isPending && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            {t('ticketDetail.forward.send')}
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        <Field id="fw-agent" label={t('ticketDetail.forward.employee')} required error={missing ? t('ticketDetail.forward.employeeRequired') : undefined}>
          <EntityPicker
            placeholder={t('ticketDetail.forward.choose')}
            options={options.map((a) => ({ value: a.id, label: a.name }))}
            value={agentId}
            onChange={setAgentId}
          />
        </Field>
        <Field id="fw-note" label={t('ticketDetail.forward.note')} hint={t('ticketDetail.composer.mode.internalAudience')}>
          <div className="relative">
            <Lock size={14} className="pointer-events-none absolute top-3 start-3 text-warning-800" aria-hidden="true" />
            <textarea
              className="input min-h-24 bg-warning-50 border-warning-300 ps-8"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              dir="auto"
            />
          </div>
        </Field>
        {mutation.isError && <FormServerError error={mutation.error} fallback={t('ticketDetail.forward.failed')} />}
      </div>
    </Dialog>
  );
}
